import { createTheme } from "@mui/material/styles";
import { attachmentStyled } from "./attachmentStyled";
import { glossaryStyled } from "./glossaryStyled";
import { infoBlockStyled } from "./infoBlockStyled";
import { scrollableTabsStyled } from "./scrollableTabsStyled";
import { searchBarStyled } from "./searchBarStyled";
import { textStyled } from "./textStyled";
import { timeLineStepStyled } from "./timeLineStepStyled";
const overrideKey = "customization";
const colors = {
    brandBlue: "#0000FF",
    brandBlueLight: "#1D23F5",
    brandBlueDark: "#0000CC",
    brandBlueSoft: "#84AAFF",
    inkMain: "#27272B",
    inkMuted: "#4F4F56",
    white: "#FFFFFF",
    black: "#000000",
    slateMain: "#2A4161",
    slateLight: "#D4E5FF",
    slateDark: "#1A2A3B",
    slateButton: "#282828",
    dangerLight: "#FF6B66",
    dangerMain: "#E32622",
    dangerDark: "#C81F1B",
    greenMain: "#0CFFC6",
    borderDefault: "#ECECEF",
    borderDisabled: "#D8E0EA",
    textDisabled: "#C2CFDD",
    surfaceDefault: "#FFFFFF",
    surfaceSection: "#F9FBFF",
    surfaceAttachment: "#D1EBFF",
    surfaceAttachmentHover: "#9CD3FD",
    surfaceDangerSubtle: "#FFF6F6",
    surfacePrimaryHover: "#EEF1FF",
    surfacePrimaryActive: "#E2E7FF",
    surfaceDangerHover: "#FFF4F3",
    surfaceDangerActive: "#FFEAE9",
    surfaceInheritHover: "#F1F3F6",
    surfaceInheritActive: "#E9EDF2",
    surfaceGrey: "#F6F6F9",
    shadowPrimary: "rgba(28, 39, 64, 0.08)",
};
const primary = {
    light: colors.brandBlueLight,
    main: colors.brandBlue,
    dark: colors.brandBlueDark,
    text: "#282828",
    contrastText: colors.white,
};
const secondary = {
    light: colors.slateLight,
    main: colors.slateMain,
    dark: colors.slateDark,
    contrastText: colors.white,
    text: colors.white,
    btnBackground: colors.slateButton,
};
const error = {
    light: colors.dangerLight,
    main: colors.dangerMain,
    dark: colors.dangerDark,
    contrastText: colors.white,
};
const border = {
    default: colors.borderDefault,
    soft: colors.brandBlueSoft,
    active: colors.greenMain,
    grey: "#E2E2E9",
    disabled: colors.borderDisabled,
    separator: colors.inkMain,
};
const common = {
    consent: {
        wrap: {
            background: colors.surfaceDefault,
            borderColor: border,
        },
    },
};
const containedPrimaryButton = ({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "[data-dark] &": {
        backgroundColor: theme.palette.primary.contrastText,
        color: theme.palette.primary.main,
        "&:hover, &:focus, &:focus-visible": {
            backgroundColor: theme.palette.primary.contrastText,
            color: theme.palette.primary.main,
        },
    },
});
const containedSecondaryButton = ({ theme }) => ({
    backgroundColor: colors.slateButton,
    color: theme.palette.secondary.contrastText,
});
const outlinedPrimaryButton = ({ theme }) => ({
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    "[data-dark] &": {
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.contrastText,
    },
});
const outlinedSecondaryButton = ({ theme }) => ({
    color: theme.palette.secondary.main,
    backgroundColor: "transparent",
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
});
const containedErrorButton = ({ theme }) => ({
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
});
const outlinedErrorButton = ({ theme }) => ({
    color: theme.palette.error.main,
    backgroundColor: colors.surfaceDefault,
    borderColor: theme.palette.error.main,
    borderWidth: 2,
});
const textPrimaryButton = ({ theme }) => ({
    color: theme.palette.text.primary,
    "&:hover, &:focus, &:focus-visible": {
        backgroundColor: "transparent",
    },
    "[data-dark] &": {
        color: theme.palette.primary.contrastText,
    },
});
const textErrorButton = ({ theme }) => ({
    color: theme.palette.error.main,
});
const typography = {
    fontFamily: "'Montserrat'",
    fontSize: 14,
    h1: {
        fontFamily: "'Montserrat'",
        fontWeight: 700,
        fontSize: "28px",
        lineHeight: "100%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "40px", // Desktop up
        },
    },
    h2: {
        fontFamily: "'Montserrat'",
        fontWeight: 700,
        fontSize: "28px",
        lineHeight: "130%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "34px", // Desktop up
        },
    },
    h3: {
        fontFamily: "'Montserrat'",
        fontWeight: 700,
        fontSize: "24px",
        lineHeight: "130%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "28px", // Desktop up
        },
    },
    h4: {
        fontFamily: "'Montserrat'",
        fontWeight: 700,
        fontSize: "20px",
        lineHeight: "130%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "24px", // Desktop up
        },
    },
    h5: {
        fontFamily: "'Montserrat'",
        fontWeight: 700,
        fontSize: "18px",
        lineHeight: "140%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "20px", // Desktop up
        },
    },
    h6: {
        fontFamily: "'Montserrat'",
        fontWeight: 700,
        fontSize: "16px",
        lineHeight: "140%",
        letterSpacing: "0.02em",
    },
    subtitle1: {
        fontFamily: "'Montserrat'",
        fontWeight: 600,
        fontSize: "16px",
        lineHeight: "150%",
        letterSpacing: "-0.02em",
    },
    subtitle2: {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "150%",
    },
    body1: {
        fontSize: "16px",
        lineHeight: "140%",
    },
    body2: {
        fontSize: "14px",
        lineHeight: "140%",
    },
    button: {
        fontFamily: "'Montserrat'",
        fontWeight: 600,
        fontSize: "16px",
        lineHeight: "150%",
        letterSpacing: "-0.02em",
        textTransform: "none",
    },
    caption: {
        fontSize: "12px",
        lineHeight: "100%",
    },
};
export default createTheme({
    colorSchemes: { dark: false },
    shape: {
        borderRadius: 0,
    },
    spacing: 8,
    palette: {
        primary,
        secondary,
        error,
        border,
        common,
        background: {
            default: colors.surfaceDefault,
            paper: colors.surfaceDefault,
            header: colors.brandBlue,
            footer: colors.brandBlue,
            main: colors.brandBlue,
            dark: colors.inkMain,
        },
        text: {
            primary: colors.inkMain,
            secondary: colors.inkMuted,
        },
    },
    typography,
    [overrideKey]: {
        Attachment: {
            container: {
                backgroundColor: colors.surfaceAttachment,
                "&:hover": {
                    backgroundColor: colors.surfaceAttachmentHover,
                },
            },
        },
        Accordion: {
            expandIcon: {
                color: primary.main,
            },
        },
    },
    components: {
        searchBarStyled: searchBarStyled,
        timeLineStepStyled: timeLineStepStyled,
        textStyled: textStyled,
        scrollableTabsStyled: scrollableTabsStyled,
        infoBlockStyled: infoBlockStyled,
        glossaryStyled: glossaryStyled,
        attachmentStyled: attachmentStyled,
        MuiLink: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.primary.main,
                    textDecorationColor: "currentColor",
                    fontFamily: "'Montserrat'",
                    "&:hover": {
                        textDecoration: "underline",
                    },
                }),
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    fontFamily: "'Montserrat'",
                    fontWeight: 600,
                    fontSize: "1rem",
                    lineHeight: "1.5rem",
                    letterSpacing: "-0.02em",
                    textTransform: "none",
                    borderRadius: 0,
                    minWidth: 72,
                    minHeight: 48,
                    padding: "12px 16px",
                    position: "relative",
                    // Permet l'animation du hover sur le label du bouton (underline)
                    "--button-label-padding-x": "16px",
                    "--button-label-bottom": "11px",
                    "--button-label-left": "var(--button-label-padding-x)",
                    "--button-label-right": "var(--button-label-padding-x)",
                    "&:has(.MuiButton-startIcon)": {
                        "--button-label-left": "calc(var(--button-label-padding-x) + 32px)",
                    },
                    "&:has(.MuiButton-endIcon)": {
                        "--button-label-right": "calc(var(--button-label-padding-x) + 32px)",
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        right: "var(--button-label-right)",
                        bottom: "var(--button-label-bottom)",
                        left: "var(--button-label-left)",
                        height: "1px",
                        backgroundColor: "currentColor",
                        pointerEvents: "none",
                        transform: "scaleX(0)",
                        transformOrigin: "left",
                        transition: "transform 200ms ease",
                    },
                    gap: 12,
                    "&::before": {
                        display: "none",
                    },
                    "&:not(.Mui-disabled):hover::after": {
                        transform: "scaleX(1)",
                    },
                    "&.Mui-disabled": {
                        color: colors.textDisabled,
                        backgroundColor: colors.surfaceSection,
                        borderColor: colors.borderDisabled,
                    },
                },
                contained: {
                    border: "none",
                },
                outlined: {
                    boxSizing: "border-box",
                    padding: "10px 14px",
                    borderWidth: 2,
                    "--button-label-padding-x": "14px",
                    "--button-label-bottom": "9px",
                    "&.MuiButton-sizeLarge": {
                        padding: "14px 18px",
                        "--button-label-padding-x": "18px",
                        "--button-label-bottom": "13px",
                    },
                    "&.MuiButton-sizeSmall": {
                        padding: "6px 12px",
                        "--button-label-padding-x": "12px",
                        "--button-label-bottom": "5px",
                    },
                },
                containedError: containedErrorButton,
                sizeLarge: {
                    minHeight: 56,
                    padding: "16px 20px",
                    fontSize: "1rem",
                    "--button-label-padding-x": "20px",
                    "--button-label-bottom": "15px",
                },
                sizeMedium: {
                    minHeight: 48,
                    padding: "12px 16px",
                    fontSize: "15px",
                },
                sizeSmall: {
                    minHeight: 40,
                    padding: "10px 14px",
                    fontSize: "0.8125rem",
                    "--button-label-padding-x": "14px",
                    "--button-label-bottom": "9px",
                },
                containedPrimary: containedPrimaryButton,
                containedSecondary: containedSecondaryButton,
                outlinedPrimary: outlinedPrimaryButton,
                outlinedSecondary: outlinedSecondaryButton,
                outlinedError: outlinedErrorButton,
                textPrimary: textPrimaryButton,
                textError: textErrorButton,
                textInherit: {
                    color: primary.text,
                },
                startIcon: {
                    marginRight: 0,
                    marginLeft: 0,
                },
                endIcon: {
                    marginRight: 0,
                    marginLeft: 0,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "[data-dark] &": {
                        color: theme.palette.primary.contrastText,
                    },
                }),
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.primary,
                }),
                subtitle2: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                }),
                body2: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                }),
                caption: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                }),
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.brandBlueSoft,
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    paddingX: { xs: 16, md: 24, lg: 32 },
                    maxWidth: "1640px !important",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                    boxShadow: `0 24px 80px ${colors.shadowPrimary}`,
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "20px 24px",
                    gap: "10px",
                    minHeight: 58,
                    backgroundColor: colors.surfaceDefault,
                    borderBottom: `2px solid ${colors.inkMain}`,
                },
                input: {
                    padding: 0,
                },
                underline: {
                    "&::before": {
                        border: "none",
                    },
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "20px 24px",
                    gap: "10px",
                    minHeight: 58,
                    backgroundColor: colors.surfaceDefault,
                    borderBottom: `2px solid ${colors.inkMain}`,
                    "&:hover": {
                        backgroundColor: colors.surfaceDefault,
                    },
                    "&.Mui-focused": {
                        backgroundColor: colors.surfaceDefault,
                    },
                    "&.Mui-error": {
                        border: `1px solid ${error.main}`,
                        borderBottom: `1px solid ${error.main}`,
                        backgroundColor: colors.surfaceDefault,
                    },
                    "&.Mui-error.Mui-focused": {
                        border: `2px solid ${error.main}`,
                        borderBottom: `2px solid ${error.main}`,
                    },
                    "&::before": {
                        borderBottom: "none",
                    },
                    "&::after": {
                        borderBottom: "none",
                    },
                },
                input: {
                    padding: 0,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontSize: "1rem",
                    minHeight: 26,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: colors.inkMain,
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontStyle: "normal",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "150%",
                    color: colors.black,
                    "&.Mui-error": {
                        color: error.main,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 0,
                    marginTop: 6,
                    fontSize: "1rem",
                    lineHeight: "1.25rem",
                    "&.Mui-error": {
                        color: error.main,
                    },
                },
                contained: {
                    marginLeft: 0,
                    marginRight: 0,
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    width: "100%",
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    color: colors.surfaceDefault,
                    backgroundColor: primary.main,
                    minHeight: 40,
                    borderRadius: 0,
                    "&.Mui-expanded": {
                        borderRadius: "10px 10px 0 0",
                    },
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    maxWidth: "auto",
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: (theme) => ({
                body: {
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    fontFamily: "'Roboto', sans-serif",
                    margin: 0,
                },
            }),
        },
    },
});
